'use client';

import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'ai-dashboard:hideEmptyWidgets';
const SAME_TAB_EVENT = 'ai-dashboard:hideEmptyWidgets-changed';

const readFromStorage = (): boolean => {
    if (typeof window === 'undefined') return true;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return true;
    return raw === 'true';
};

export function useHideEmptyWidgets(): [boolean, (next: boolean) => void] {
    const [enabled, setEnabledState] = useState<boolean>(true);

    useEffect(() => {
        setEnabledState(readFromStorage());

        const sync = () => setEnabledState(readFromStorage());
        const onStorage = (e: StorageEvent) => {
            if (e.key === STORAGE_KEY) sync();
        };

        window.addEventListener('storage', onStorage);
        window.addEventListener(SAME_TAB_EVENT, sync);
        return () => {
            window.removeEventListener('storage', onStorage);
            window.removeEventListener(SAME_TAB_EVENT, sync);
        };
    }, []);

    const setEnabled = useCallback((next: boolean) => {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem(STORAGE_KEY, String(next));
        setEnabledState(next);
        window.dispatchEvent(new Event(SAME_TAB_EVENT));
    }, []);

    return [enabled, setEnabled];
}
